/**
 * Cacahuete is not particularly RESTful, but we try to organize this file in a RESTful way by
 * entity type. Generally, the top level object has keys that are types of entities (think:
 * database tables). Under those keys are RESTful instance (key: "one") and collection routes
 * (key: "all"), as well as other routes that are not RESTful but operate on that entity
 * (eg, "clone").
 *
 * Some routes won't fit in this paradigm so put them in the most logical place. For endpoints that
 * share the same route but use different HTTP verbs, just add the route once.
 */
const getCommentsBaseUrl = (projectUid, type, targetUid) => {
  if (type === "project" || !targetUid) {
    return `/v2/project/${projectUid}/comments`;
  } else {
    return `/v2/project/${projectUid}/${type}/${targetUid}/comments`;
  }
};
export default {
  projects: {
    all: () => "/v2/projects",
    one: projectUid => `/v2/project/${projectUid}`,
    clone: projectUid => `/v2/project/${projectUid}/clone`,
    users: projectUid => `/v2/project/${projectUid}/users`,
  },
  profile: {
    one: projectUid => `/v2/project/${projectUid}/profile`,
  },
  punchList: {
    statsForProject: projectUid => `/v2/project/${projectUid}/punchlists/stats`,
  },
  photos: {
    allInProject: projectUid => `/v2/project/${projectUid}/photos/filter`,
    allInProjectMeta: projectUid => `/v2/project/${projectUid}/photos_meta`,
    oneInProject: (projectUid, photoUid) =>
      `/v2/project/${projectUid}/photos/${photoUid}`,
    oneAssetInProject: (projectUid, photoUid, fileType) =>
      `/v2/project/${projectUid}/photo/${photoUid}/asset/${fileType}`,
    oneShareableInProject: (projectUid, photoUid) =>
      `/v7/project/${projectUid}/photos/${photoUid}/share`,
  },
  documents: {
    allInProject: projectUid => `/v3/project/${projectUid}/documents`,
    oneInProject: (projectUid, documentUid) =>
      `/v3/project/${projectUid}/documents/${documentUid}`,
    oneInProjectSecure: (projectUid, documentUid) =>
      `/v4/project/${projectUid}/documents/${documentUid}`,
  },
  // Cacahuete calls these "annotations", but we have standardized on "markups" in PlanWeb
  markups: {
    allInProjectSheet: (projectUid, sheetUid) =>
      `/v2/project/${projectUid}/page/${sheetUid}/annotations`,
    allInProject: projectUid => `/v2/project/${projectUid}/annotations`,
    stampInProject: projectUid => `/v2/project/${projectUid}/annotation/punch`,
  },
  issueUploads: {
    asset: (projectUid, uploadUid) =>
      `/caca/v5/project/${projectUid}/issues/upload/${uploadUid}/asset`,
    oneInProject: (projectUid, uploadUid) =>
      `/caca/v5/project/${projectUid}/issues/upload/${uploadUid}`,
  },
  issues: {
    oneInProject: (projectUid, issueUid) =>
      `/v7/project/${projectUid}/punchlists/${issueUid}`,
    allInProject: projectUid => `/v2/project/${projectUid}/punchlists`,
    batchInProject: projectUid => `/v2/project/${projectUid}/punchlists/batch`,
    allDefaultsInProject: projectUid =>
      `/v2/project/${projectUid}/stamp_defaults`,
    allListsInProject: projectUid => `/v3/project/${projectUid}/issue_lists`,
    allMetaInProject: projectUid =>
      `/v3/project/${projectUid}/punchlists/stamp_meta`,
    filterAllInProject: projectUid =>
      `/v5/project/${projectUid}/punchlists/filter`,
  },
  issueLists: {
    oneInProject: (projectUid, issueListUid) =>
      `/v3/project/${projectUid}/issue_lists/${issueListUid}`,
  },
  attachments: {
    oneInProject: (projectUid, attachmentUid) =>
      `/v2/project/${projectUid}/attach/${attachmentUid}`,
    oneAssetInProject: (projectUid, attachmentUid) =>
      `/v2/project/${projectUid}/attach/${attachmentUid}/asset`,
    oneAssetInProjectSecure: (projectUid, attachmentUid) =>
      `/v4/project/${projectUid}/attach/${attachmentUid}/asset`,
    allInProject: projectUid => `/v2/project/${projectUid}/attach`,
  },
  sheets: {
    allInProject: projectUid => `/v3/project/${projectUid}/sheets`,
    oneInProject: (projectUid, sheetUid) =>
      `/v2/project/${projectUid}/sheets/${sheetUid}`,
    filterInProject: projectUid => `/v2/project/${projectUid}/sheets_filter`,
    referenceDataInProject: projectUid =>
      `/v2/project/${projectUid}/sheets_ref`,
    pageInProject: projectUid => `/v2/project/${projectUid}/page`,
    metaInProject: projectUid => `/v2/project/${projectUid}/sheet_meta`,
  },
  sheetTags: {
    allInProject: projectUid => `/v3/project/${projectUid}/tags`,
  },
  snapshots: {
    allInProject: projectUid => `/v2/project/${projectUid}/snapshots`,
    oneInProject: (projectUid, snapshotUid) =>
      `/v2/project/${projectUid}/snapshots/${snapshotUid}`,
    emailInProject: projectUid => `/v2/project/${projectUid}/snapshots/email`,
  },
  staples: {
    all: projectUid => `/v3/project/${projectUid}/staples`,
    one: (projectUid, stapleUid) =>
      `/v3/project/${projectUid}/staples/${stapleUid}`,
  },
  users: {
    users: () => "/v2/users",
    emailAvailability: () => "/v2/user/email_availability",
    projectUsers: projectUid => `/v2/project/${projectUid}/users`,
    deletion: () => "/v2/users/deletion",
    resetPassword: () => "/v3/password_reset",
    forgeMapping: projectUid => `/v7/project/${projectUid}/users-forge-mapping`,
  },
  events: {
    allInProject: projectUid => `/v3/project/${projectUid}/events`,
  },
  fieldReports: {
    oneInProjectForType: (projectUid, fieldReportTypeUid, fieldReportUid) =>
      `/v2/project/${projectUid}/field-report-types/${fieldReportTypeUid}/reports/${fieldReportUid}`,
    allInProjectForType: (projectUid, fieldReportTypeUid) =>
      `/v2/project/${projectUid}/field-report-types/${fieldReportTypeUid}/reports`,
  },
  fieldReportTypes: {
    allInProject: projectUid => `/v2/project/${projectUid}/field-report-types`,
    oneInProject: (projectUid, fieldReportTypeUid) =>
      `/v2/project/${projectUid}/field-report-types/${fieldReportTypeUid}`,
    countsForProject: projectUid =>
      `/v3/project/${projectUid}/field-report-types/counts`,
    countsForProjectType: (projectUid, fieldReportTypeUid) =>
      `/v3/project/${projectUid}/field-report-types/${fieldReportTypeUid}/counts`,
  },
  fieldReportDefaultTemplates: {
    all: () => "/v3/field_report_default_templates",
  },
  fieldReportFiles: {
    oneInProject: (projectUid, fieldReportFileUid) =>
      `/v2/project/${projectUid}/field-report-files/${fieldReportFileUid}`,
    allInProject: projectUid => `/v2/project/${projectUid}/field-report-files`,
  },
  fieldReportTemplates: {
    oneInProject: (projectUid, fileUid) =>
      `/v2/project/${projectUid}/field-report-templates/${fileUid}`,
  },
  rfis: {
    allInProject: projectUid => `/v2/project/${projectUid}/rfis`,
    allLabelsInProject: projectUid => `/v2/project/${projectUid}/rfi_status`,
  },
  featureFlags: { all: () => `/v3/feature_flags` },
  orgProjects: {
    all: orgId => `/v3/organizations/${orgId}/projects`,
  },
  versionSets: {
    allInProject: projectUid => `/v2/project/${projectUid}/version_sets`,
  },
  email: {
    send: () => `/v2/email`,
  },
  pgForms: {
    all: projectUid => `/v5/project/${projectUid}/pg-forms`,
  },
  resources: {
    allInProject: (projectUid, resourceType) =>
      `/v2/project/${projectUid}/resources/${resourceType}/filter`,
  },
  comments: {
    all: (projectUid, type, targetUid) =>
      getCommentsBaseUrl(projectUid, type, targetUid),
    one: (projectUid, commentUid, type, targetUid) => {
      return `${getCommentsBaseUrl(projectUid, type, targetUid)}/${commentUid}`;
    },
  },
  messageAcknowledgments: {
    one: () => "/v3/message_acknowledgments",
  },
};
